/**
 * Progreso de capítulos: `MetaState.chapterProgress` guarda cuántos
 * capítulos ya se completaron (0 = ninguno, arranca en el capítulo 0). Los
 * capítulos ya completados se pueden re-jugar libremente para farmear.
 */

import type { ChapterDef, MetaState } from '../core/types';
import { chapters as chapterDefs } from '../data';

/** Cantidad de capítulos desbloqueados (siempre al menos 1 si hay capítulos definidos). */
export function unlockedChapterCount(meta: MetaState, chapters: ChapterDef[] = chapterDefs): number {
  return Math.min(chapters.length, meta.chapterProgress + 1);
}

/** ¿El capítulo en esa posición (0-indexada) está desbloqueado? */
export function isChapterUnlocked(chapterIndex: number, meta: MetaState, chapters: ChapterDef[] = chapterDefs): boolean {
  return chapterIndex < unlockedChapterCount(meta, chapters);
}

/** El capítulo que se debería ofrecer por defecto al pulsar PELEAR: el siguiente sin completar, o el último si ya se completaron todos. */
export function getCurrentChapter(meta: MetaState, chapters: ChapterDef[] = chapterDefs): ChapterDef {
  if (chapters.length === 0) {
    throw new Error('chapters: no hay capítulos definidos en data/chapters.json');
  }
  const index = Math.min(meta.chapterProgress, chapters.length - 1);
  return chapters[index] as ChapterDef;
}

/** Índice (0-indexado) del capítulo dado por id, o -1 si no existe. */
export function findChapterIndex(chapterId: string, chapters: ChapterDef[] = chapterDefs): number {
  return chapters.findIndex((c) => c.id === chapterId);
}

/**
 * Marca como completado el capítulo `chapterIndex`, avanzando el progreso
 * solo si era justo el capítulo actual (evita retroceder el progreso al
 * re-jugar un capítulo anterior ya completado). Capea en el último capítulo
 * disponible, permitiendo re-jugarlo indefinidamente para farmear.
 */
export function advanceChapterProgress(
  meta: MetaState,
  chapterIndex: number,
  chapters: ChapterDef[] = chapterDefs
): MetaState {
  if (chapterIndex !== meta.chapterProgress) return meta;
  const maxProgress = Math.max(0, chapters.length - 1);
  const chapterProgress = Math.min(chapterIndex + 1, maxProgress);
  if (chapterProgress === meta.chapterProgress) return meta;
  return { ...meta, chapterProgress };
}
